import { FormEvent, useEffect, useState } from 'react';
import type { Address } from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { t } from '../../i18n/pt';
import { api, extractError } from '../../lib/api';
import { formatPostalCodePT } from '../../lib/format';
import { Button } from '../../components/ui/Button';
import { Icon } from '../../components/ui/Icon';
import { AccountPageHeader } from '../../components/account/AccountPageHeader';
import { Field } from '../../components/ui/Field';
import { useFormErrors } from '../../hooks/useFormErrors';
import { postalCodePT, required } from '../../lib/validate';
import styles from './AddressesPage.module.scss';

type AddressFieldKey =
  | 'firstName'
  | 'lastName'
  | 'addressLine1'
  | 'postalCode'
  | 'city'
  | 'district';

interface AddressForm {
  label: string;
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  district: string;
  country: string;
  isDefaultShipping: boolean;
  isDefaultBilling: boolean;
}

const emptyForm: AddressForm = {
  label: '',
  firstName: '',
  lastName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postalCode: '',
  district: '',
  country: 'PT',
  isDefaultShipping: false,
  isDefaultBilling: false,
};

function addressToForm(a: Address): AddressForm {
  return {
    label: a.label ?? '',
    firstName: a.firstName,
    lastName: a.lastName,
    addressLine1: a.addressLine1,
    addressLine2: a.addressLine2 ?? '',
    city: a.city,
    postalCode: a.postalCode,
    district: a.district,
    country: a.country,
    isDefaultShipping: a.isDefaultShipping,
    isDefaultBilling: a.isDefaultBilling,
  };
}

type Feedback = { kind: 'success' | 'error'; text: string } | null;

export default function AddressesPage(): JSX.Element {
  const [items, setItems] = useState<Address[]>([]);
  const [listFeedback, setListFeedback] = useState<Feedback>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<AddressForm>(emptyForm);
  const { errors, setErrors, clearError } = useFormErrors<AddressFieldKey>();

  const updateField = <K extends keyof AddressForm>(key: K, value: AddressForm[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key as AddressFieldKey]) clearError(key as AddressFieldKey);
  };

  const validate = (): Partial<Record<AddressFieldKey, string>> => {
    const next: Partial<Record<AddressFieldKey, string>> = {};
    const firstNameErr = required(form.firstName);
    if (firstNameErr) next.firstName = firstNameErr;
    const lastNameErr = required(form.lastName);
    if (lastNameErr) next.lastName = lastNameErr;
    const addr1Err = required(form.addressLine1);
    if (addr1Err) next.addressLine1 = addr1Err;
    const postalReq = required(form.postalCode);
    if (postalReq) next.postalCode = postalReq;
    else {
      const postalErr = postalCodePT(form.postalCode);
      if (postalErr) next.postalCode = postalErr;
    }
    const cityErr = required(form.city);
    if (cityErr) next.city = cityErr;
    const districtErr = required(form.district);
    if (districtErr) next.district = districtErr;
    return next;
  };

  const load = async (): Promise<void> => {
    const response = await api.get<Address[]>('/account/addresses');
    setItems(response.data);
  };

  useEffect(() => {
    void load();
  }, []);

  const startAdd = (): void => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError(null);
    setListFeedback(null);
    setErrors({});
    setShowForm(true);
  };

  const startEdit = (a: Address): void => {
    setEditingId(a.id);
    setForm(addressToForm(a));
    setFormError(null);
    setListFeedback(null);
    setErrors({});
    setShowForm(true);
  };

  const cancel = (): void => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError(null);
    setErrors({});
    setShowForm(false);
  };

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setFormError(null);
    const payload = {
      ...form,
      addressLine2: form.addressLine2 || null,
      label: form.label || null,
    };
    const wasEditing = editingId !== null;
    try {
      if (editingId) {
        await api.put(`/account/addresses/${editingId}`, payload);
      } else {
        await api.post('/account/addresses', payload);
      }
      cancel();
      await load();
      setListFeedback({
        kind: 'success',
        text: wasEditing ? 'Morada atualizada.' : 'Morada guardada.',
      });
    } catch (err) {
      setFormError(extractError(err, 'Falha ao guardar morada'));
    }
  };

  const remove = async (id: string): Promise<void> => {
    if (editingId === id) cancel();
    try {
      await api.delete(`/account/addresses/${id}`);
      await load();
      setListFeedback({ kind: 'success', text: 'Morada eliminada.' });
    } catch (err) {
      setListFeedback({
        kind: 'error',
        text: extractError(err, 'Falha ao eliminar morada'),
      });
    }
  };

  const setRoleDefault = async (
    a: Address,
    role: 'shipping' | 'billing',
  ): Promise<void> => {
    setListFeedback(null);
    try {
      const patch =
        role === 'shipping' ? { isDefaultShipping: true } : { isDefaultBilling: true };
      await api.put(`/account/addresses/${a.id}`, patch);
      await load();
      setListFeedback({
        kind: 'success',
        text:
          role === 'shipping'
            ? 'Morada definida como entrega padrão.'
            : 'Morada definida como faturação padrão.',
      });
    } catch (err) {
      setListFeedback({
        kind: 'error',
        text: extractError(err, 'Falha ao definir morada padrão'),
      });
    }
  };

  return (
    <>
      <SEO title={t.account.addresses} noindex />
      <AccountPageHeader
        title={t.account.addresses}
        lead="Gere as moradas de entrega e faturação guardadas na tua conta."
      />

      {listFeedback ? (
        <div className={styles.messages} role="status" aria-live="polite">
          <p
            className={listFeedback.kind === 'success' ? styles.success : styles.error}
          >
            {listFeedback.text}
          </p>
        </div>
      ) : null}

      {items.length === 0 ? (
        <p className={styles.empty}>Ainda não tens moradas guardadas.</p>
      ) : (
        <ul className={styles.list}>
          {items.map((a) => {
            const isDefault = a.isDefaultShipping || a.isDefaultBilling;
            return (
              <li
                key={a.id}
                className={`${styles.card} ${isDefault ? styles.cardDefault : ''}`}
              >
                <div className={styles.cardHeader}>
                  <h3 className={styles.cardTitle}>
                    {a.label ?? `${a.firstName} ${a.lastName}`}
                  </h3>
                  <div className={styles.cardIcons}>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => startEdit(a)}
                      aria-label={`Editar morada ${a.label ?? a.firstName}`}
                    >
                      <Icon name="pencil" size={16} />
                    </button>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => { void remove(a.id); }}
                      aria-label={`Eliminar morada ${a.label ?? a.firstName}`}
                    >
                      <Icon name="trash" size={16} />
                    </button>
                  </div>
                </div>

                <div className={styles.cardBody}>
                  {a.label ? <p>{a.firstName} {a.lastName}</p> : null}
                  <p>{a.addressLine1}{a.addressLine2 ? `, ${a.addressLine2}` : ''}</p>
                  <p>{a.postalCode} {a.city}</p>
                  {a.district ? <p>{a.district}</p> : null}
                </div>

                {isDefault ? (
                  <div className={styles.badges}>
                    {a.isDefaultShipping ? (
                      <span className={styles.badge}>
                        <Icon name="check" size={12} />
                        Entrega padrão
                      </span>
                    ) : null}
                    {a.isDefaultBilling ? (
                      <span className={styles.badge}>
                        <Icon name="check" size={12} />
                        Faturação padrão
                      </span>
                    ) : null}
                  </div>
                ) : null}

                {!a.isDefaultShipping || !a.isDefaultBilling ? (
                  <div className={styles.cardActions}>
                    {!a.isDefaultShipping ? (
                      <button
                        type="button"
                        className={styles.setDefault}
                        onClick={() => { void setRoleDefault(a, 'shipping'); }}
                      >
                        Definir como entrega padrão
                      </button>
                    ) : null}
                    {!a.isDefaultBilling ? (
                      <button
                        type="button"
                        className={styles.setDefault}
                        onClick={() => { void setRoleDefault(a, 'billing'); }}
                      >
                        Definir como faturação padrão
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {!showForm ? (
        <div className={styles.addCta}>
          <Button variant="primary" onClick={startAdd}>
            Adicionar morada
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className={styles.form} noValidate>
          <h2 className={styles.formTitle}>
            {editingId ? 'Editar morada' : 'Nova morada'}
          </h2>

          <Field label="Etiqueta" className={styles.field}>
            <input
              value={form.label}
              placeholder="ex: Casa, Trabalho"
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
          </Field>

          <div className={styles.row}>
            <Field label="Nome" required error={errors.firstName} className={styles.field}>
              <input
                value={form.firstName}
                onChange={(e) => updateField('firstName', e.target.value)}
                autoComplete="given-name"
              />
            </Field>
            <Field label="Apelido" required error={errors.lastName} className={styles.field}>
              <input
                value={form.lastName}
                onChange={(e) => updateField('lastName', e.target.value)}
                autoComplete="family-name"
              />
            </Field>
          </div>

          <Field label="Morada" required error={errors.addressLine1} className={styles.field}>
            <input
              value={form.addressLine1}
              onChange={(e) => updateField('addressLine1', e.target.value)}
              autoComplete="address-line1"
            />
          </Field>

          <Field label="Linha 2 (opcional)" className={styles.field}>
            <input
              value={form.addressLine2}
              onChange={(e) => setForm({ ...form, addressLine2: e.target.value })}
              autoComplete="address-line2"
            />
          </Field>

          <div className={styles.rowPostalCity}>
            <Field
              label="Código postal"
              required
              error={errors.postalCode}
              className={styles.field}
            >
              <input
                value={form.postalCode}
                placeholder="0000-000"
                onChange={(e) =>
                  updateField('postalCode', formatPostalCodePT(e.target.value))
                }
                autoComplete="postal-code"
                inputMode="numeric"
                maxLength={8}
              />
            </Field>
            <Field label="Localidade" required error={errors.city} className={styles.field}>
              <input
                value={form.city}
                onChange={(e) => updateField('city', e.target.value)}
                autoComplete="address-level2"
              />
            </Field>
          </div>

          <Field label="Distrito" required error={errors.district} className={styles.field}>
            <input
              value={form.district}
              onChange={(e) => updateField('district', e.target.value)}
              autoComplete="address-level1"
            />
          </Field>

          <div className={styles.checkboxes}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={form.isDefaultShipping}
                onChange={(e) => setForm({ ...form, isDefaultShipping: e.target.checked })}
              />
              Definir como morada de entrega padrão
            </label>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={form.isDefaultBilling}
                onChange={(e) => setForm({ ...form, isDefaultBilling: e.target.checked })}
              />
              Definir como morada de faturação padrão
            </label>
          </div>

          {formError ? (
            <p className={styles.error} role="alert" aria-live="polite">
              {formError}
            </p>
          ) : null}

          <div className={styles.actions}>
            <Button type="submit" variant="primary">{t.common.save}</Button>
            <Button type="button" variant="secondary" onClick={cancel}>
              {t.common.cancel}
            </Button>
          </div>
        </form>
      )}
    </>
  );
}
